/**
 * useClickOrDoubleClick - Distinguish between single and double clicks
 *
 * Single clicks are delayed to allow double-clicks to cancel them.
 */

import { useCallback, useRef, useEffect } from 'react';

interface UseClickOrDoubleClickOptions {
  onClick: () => void;
  onDoubleClick: () => void;
  delay?: number;
}

export function useClickOrDoubleClick({ onClick, onDoubleClick, delay = 200 }: UseClickOrDoubleClickOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    timeoutRef.current = setTimeout(() => {
      onClick();
    }, delay);
  }, [onClick, delay]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    onDoubleClick();
  }, [onDoubleClick]);

  return { handleClick, handleDoubleClick };
}
